// The learner's front door: a phone number and a password.
//
// That is how staff add people now — by number, with a password generated for
// them — so the number field comes first and carries the fixed +998 every
// Uzbek number starts with. A small switch swaps it for a plain login box, for
// the people who registered with a username before any of this and still sign
// in with it; the server reads whatever is in that box either way.

import { useEffect, useId, useRef, useState } from 'react'
import type { FormEvent, ReactNode, Ref } from 'react'
import { Link } from 'react-router-dom'

import { ApiError } from '../api'
import { useAuth } from '../auth'
import { useT } from '../i18n'
import auth from '../i18n/auth'
import { clock, lockOf, useLockClock } from '../lib/lock'
import { Icon, Logo, PhoneInput, SignedOutFrame, messageOf, toE164Uz } from '../lib/ui'
import { PRODUCT_NAME, usePlatform } from '../platform'
import '../styles/auth.css'

type Mode = 'phone' | 'username'
type Field = 'login' | 'password'
type Errors = Partial<Record<Field, string>>

/** Where this browser remembers which of the two boxes somebody signs in with. */
const MODE_KEY = 'mymentor.signin.mode'

/** How many digits follow +998. */
const NATIONAL_DIGITS = 9

export function SignIn() {
  const t = useT(auth)
  const { signIn, signedOutReason } = useAuth()
  const id = useId()

  const [mode, setMode] = useState<Mode>(readMode)
  const [digits, setDigits] = useState('')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [errors, setErrors] = useState<Errors>({})
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // The learner was signed out here because they signed in on another device
  // (one session at a time; auth's signedOutReason). Said in the alert box
  // until they try to sign in again — from then on the answer to that attempt
  // is the news — and translated as it is shown, so switching the language
  // switches it too.
  const [replacedShown, setReplacedShown] = useState(signedOutReason === 'replaced')
  useEffect(() => {
    if (signedOutReason === 'replaced') setReplacedShown(true)
  }, [signedOutReason])

  // The account is locked (423): counted down to the moment it answers again,
  // since a countdown says how long to wait where a timestamp only says when
  // (lib/lock.ts).
  const { lock, setLock, remaining, lockedFor, counting } = useLockClock()

  const loginInput = useRef<HTMLInputElement | null>(null)
  const passwordInput = useRef<HTMLInputElement | null>(null)

  // The form stays usable while the account is locked, and the message says
  // why. A lock refuses every browser but one kind, which gets a single try
  // while the lock lasts: a browser holding a device of this account (its
  // device cookie). It earned one by using a password of the person's own on
  // this site within the last 90 days — a sign-in with a password somebody
  // else issued earns none — and has not lost it since to somebody else
  // signing in on it, to the password being changed in another browser or
  // reset by staff or the operator, or to five other browsers signing in to
  // the account more recently (internal/auth/device.go; the note,
  // auth.lockedKnownDevice, says it in a person's words). This may be such a
  // browser — its owner may have set the lock off here by mistyping. Only the
  // server knows whose browser it is; a stranger's attempt is refused without
  // the password being looked at, in the same words.

  // Switching mounts the other box, and its autoFocus takes the caret there.
  // A lock belongs to the login typed before, so it goes with the box.
  function switchMode() {
    const next: Mode = mode === 'phone' ? 'username' : 'phone'
    setMode(next)
    rememberMode(next)
    setErrors({})
    setNotice(null)
    setLock(null)
    setReplacedShown(false)
  }

  function focusLogin() {
    if (mode === 'username') loginInput.current?.focus()
    else document.getElementById(`${id}-phone`)?.focus()
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (busy) return

    const found: Errors = {}
    let login = ''
    if (mode === 'phone') {
      if (digits.length === 0) found.login = t('enterPhone')
      else if (digits.length < NATIONAL_DIGITS) found.login = t('phoneIncomplete')
      else login = toE164Uz(digits)
    } else {
      // Nothing about the shape is checked: this box is where somebody who
      // registered under an older rule — or who types their number here after
      // all — still has to be able to get in. The server decides.
      login = username.trim()
      if (!login) found.login = t('enterUsername')
    }
    if (!password) found.password = t('enterPassword')
    setErrors(found)
    // A lock already on screen stays: an empty box is no answer from the
    // server, and while the request runs, clearing it would flash the form's
    // normal state for as long as a refusal that ends in it again takes.
    setNotice(null)
    setReplacedShown(false)
    if (found.login || found.password) {
      if (found.login) focusLogin()
      else passwordInput.current?.focus()
      return
    }

    setBusy(true)
    try {
      await signIn(login, password)
      // No navigation here: once the user is set, App swaps the whole tree.
    } catch (err) {
      const locked = lockOf(err)
      setLock(locked)
      setNotice(locked ? null : explain(err))
      // The login is almost never the mistake, and retyping it is a chore.
      setPassword('')
      passwordInput.current?.focus()
    } finally {
      setBusy(false)
    }
  }

  /** The refusal in words, told the way this form asked. */
  function explain(err: unknown): string {
    if (err instanceof ApiError) {
      // The server does not say whether the login or the password was wrong,
      // and this does not guess: it names the two things that were typed.
      if (err.code === 'bad_credentials') {
        return t(mode === 'phone' ? 'badPhoneCredentials' : 'badUsernameCredentials')
      }
      // At the door, a plain 403 means the account itself is stopped. Other
      // refusals that share the status (a request from another site, say) are
      // not that, and read as themselves.
      if (err.status === 403 && err.code === 'forbidden') return t('suspended')
    }
    return messageOf(err, auth)
  }

  const loginError = errors.login
  const loginErrorId = `${id}-login-error`

  return (
    <SignedOutFrame>
      <div className="login auth">
        <div className="login__card">
          <AuthBrand />
          <h1 className="auth__title">{t('signInTitle')}</h1>
          <p className="auth__lead">{t(mode === 'phone' ? 'signInLead' : 'signInLeadUsername')}</p>

          <form onSubmit={submit} noValidate>
            {mode === 'phone' ? (
              <div className="auth-field">
                <label className="auth-field__label" htmlFor={`${id}-phone`}>
                  {t('phoneLabel')}
                </label>
                <PhoneInput
                  id={`${id}-phone`}
                  value={digits}
                  onChange={setDigits}
                  autoFocus
                  invalid={Boolean(loginError)}
                  label={t('phoneLabel')}
                />
                {/* The shared phone field takes no description, so its
                    complaint is announced as it appears instead. */}
                {loginError && (
                  <p className="auth-field__error" id={loginErrorId} role="alert">
                    {loginError}
                  </p>
                )}
              </div>
            ) : (
              <div className="auth-field">
                <label className="auth-field__label" htmlFor={`${id}-username`}>
                  {t('usernameLabel')}
                </label>
                <input
                  ref={loginInput}
                  id={`${id}-username`}
                  type="text"
                  autoComplete="username"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  autoFocus
                  maxLength={64}
                  placeholder={t('usernamePlaceholder')}
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  aria-invalid={loginError ? true : undefined}
                  aria-describedby={loginError ? loginErrorId : undefined}
                />
                {loginError && (
                  <p className="auth-field__error" id={loginErrorId}>
                    {loginError}
                  </p>
                )}
              </div>
            )}

            <button type="button" className="link auth__switch" onClick={switchMode}>
              {t(mode === 'phone' ? 'useUsername' : 'usePhone')}
            </button>

            <PasswordField
              id={`${id}-password`}
              label={t('passwordLabel')}
              value={password}
              onChange={setPassword}
              autoComplete="current-password"
              error={errors.password}
              inputRef={passwordInput}
            />

            {lock && (
              <div className="auth__alert auth__alert--lock" role="alert">
                {counting ? (
                  <p>
                    <span className="visually-hidden">{t('lockedCountdown', { time: clock(lockedFor) })}</span>
                    <span aria-hidden="true">{t('lockedCountdown', { time: clock(remaining) })}</span>
                  </p>
                ) : (
                  <p>{t('lockedLater')}</p>
                )}
                <p className="auth__alert-note">{t('lockedKnownDevice')}</p>
              </div>
            )}
            {!lock && (notice || replacedShown) && (
              <p className="auth__alert" role="alert">
                {notice ?? t('sessionReplaced')}
              </p>
            )}

            <button type="submit" className="button button--wide" disabled={busy}>
              {busy ? t('signingIn') : t('signIn')}
            </button>
          </form>

          {/* Nobody can reset their own password here: there is no SMS to
              prove the number is theirs. Staff issue a temporary one from
              the learner's page, and the first sign-in with it asks for a
              password of the learner's own. */}
          <details className="auth__forgot">
            <summary className="link">{t('forgotTitle')}</summary>
            <p>{t('forgotBody')}</p>
          </details>

          <div className="auth__hint">
            <strong>{t('firstTimeTitle')}</strong>
            <p>{t('firstTimeBody')}</p>
          </div>

          <div className="auth__actions">
            <span className="muted">{t('noAccount')}</span>
            <Link className="link" to="/register">
              {t('register')}
            </Link>
          </div>
        </div>
      </div>
    </SignedOutFrame>
  )
}

// ------------------------------------------------------- shared by the doors --

/**
 * The tenant at the top of the card: its logo, or its name, or — for a tenant
 * that has neither of its own — MyMentor's logo.
 */
export function AuthBrand() {
  const platform = usePlatform()
  const dark = useDarkSurface()
  return (
    <div className="auth__brand">
      {platform.logo_path ? (
        <img src={platform.logo_path} alt={platform.name} />
      ) : platform.name === PRODUCT_NAME ? (
        <Logo on={dark ? 'dark' : 'light'} />
      ) : (
        <span className="auth__brand-name">{platform.name}</span>
      )}
    </div>
  )
}

/**
 * A password box with a switch that shows what was typed, a label, and either
 * a hint or the complaint about it underneath.
 */
export function PasswordField({
  id,
  label,
  value,
  onChange,
  autoComplete,
  error,
  hint,
  inputRef,
  disabled,
  describedBy,
}: {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
  autoComplete: 'current-password' | 'new-password'
  error?: string
  hint?: ReactNode
  inputRef?: Ref<HTMLInputElement>
  disabled?: boolean
  /** Something else on the page that describes this input — the rules it must meet. */
  describedBy?: string
}) {
  const t = useT(auth)
  const [shown, setShown] = useState(false)
  const own = error ? `${id}-error` : hint ? `${id}-hint` : undefined
  const described = [own, describedBy].filter(Boolean).join(' ') || undefined

  return (
    <div className="auth-field">
      <label className="auth-field__label" htmlFor={id}>
        {label}
      </label>
      <div className="password">
        <input
          ref={inputRef}
          id={id}
          type={shown ? 'text' : 'password'}
          autoComplete={autoComplete}
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          aria-invalid={error ? true : undefined}
          aria-describedby={described}
        />
        <button
          type="button"
          className={shown ? 'password__toggle is-on' : 'password__toggle'}
          aria-label={t(shown ? 'hidePassword' : 'showPassword')}
          aria-controls={id}
          title={t(shown ? 'hidePassword' : 'showPassword')}
          disabled={disabled}
          onClick={() => setShown((s) => !s)}
        >
          <Icon name="eye" size={18} />
        </button>
      </div>
      {error ? (
        <p className="auth-field__error" id={`${id}-error`}>
          {error}
        </p>
      ) : (
        hint && (
          <p className="auth-field__hint" id={`${id}-hint`}>
            {hint}
          </p>
        )
      )}
    </div>
  )
}

// ------------------------------------------------------------------ helpers --

/**
 * Whether the page is drawn dark, so the logo can be the one drawn for a dark
 * surface: the green one all but disappears on the dark card. Follows the
 * system setting as it changes, and a theme the root element forces.
 */
function useDarkSurface(): boolean {
  const [dark, setDark] = useState(isDark)
  useEffect(() => {
    const list = window.matchMedia(DARK_QUERY)
    const update = () => setDark(isDark())
    list.addEventListener('change', update)
    return () => list.removeEventListener('change', update)
  }, [])
  return dark
}

const DARK_QUERY = '(prefers-color-scheme: dark)' // i18n-ignore: a media query, never shown

function isDark(): boolean {
  const forced = document.documentElement.dataset.theme
  if (forced === 'dark') return true
  if (forced === 'light') return false
  return window.matchMedia(DARK_QUERY).matches
}

function readMode(): Mode {
  try {
    return localStorage.getItem(MODE_KEY) === 'username' ? 'username' : 'phone'
  } catch {
    return 'phone'
  }
}

function rememberMode(mode: Mode) {
  try {
    if (mode === 'phone') localStorage.removeItem(MODE_KEY)
    else localStorage.setItem(MODE_KEY, mode)
  } catch {
    // Storage switched off: the choice lasts as long as the page does.
  }
}
